<template>
  <div
    id="sidebarContainer"
    class="d-none d-md-block h-100"
    :class="sidebarContainerClasses"
    role="navigation"
  >
    <div
      class="d-flex justify-content-center align-items-center"
      :class="{
        'sidebar-logo-expanded': sidebarExpanded,
        'sidebar-logo-collapsed': !sidebarExpanded,
      }"
    >
      <slot name="logo"></slot>
    </div>
    <div class="list-group">
      <!-- sidebar items-->
      <div v-for="item in items" :key="item.name">
        <router-link :to="'/' + item.href">
          <a
            :name="item.id"
            :class="{ activeItem: isActive(item.id) }"
            aria-expanded="false"
            :aria-label="item.name"
            class="list-group-item list-group-item-action d-flex align-items-center"
            @click="itemClick(item.id)"
          >
            <div class="d-flex justify-content-start align-items-center">
              <ips-icon v-if="item.icon" :icon="item.icon"></ips-icon>
              <span class="menu-expanded" :class="expandedClasses"
                >{{ item.name }}
                <span
                  v-if="item.badgeCount"
                  :class="{
                    'badge-primary': !isActive(item.id),
                    'badge-secondary': isActive(item.id),
                  }"
                  class="badge rounded-pill ms-2"
                  >{{ item.badgeCount }}</span
                ></span
              >
            </div>
          </a>
        </router-link>

        <!-- submenu items-->
        <div
          v-if="item.items"
          :id="item.href"
          class="collapse sidebar-submenu"
          :class="expandedClasses"
        >
          <div v-for="subItem in item.items" :key="subItem.href">
            <router-link
              :to="'/' + item.href + '/' + subItem.href"
              :class="{ activeItem: isActive(subItem.id) }"
              class="list-group-item list-group-item-action d-flex align-items-center"
              :aria-label="subItem.name"
              @click.native="itemClick(subItem.id)"
            >
              <ips-icon
                v-if="subItem.icon"
                :icon="subItem.icon"
                :class="{ activeItem: isActive(subItem.id) }"
              ></ips-icon>
              <span class="menu-expanded" :class="{ activeItem: isActive(subItem.id) }">{{
                subItem.name
              }}</span>
            </router-link>
          </div>
        </div>
      </div>
      <div class="list-group" style="position: absolute; bottom: 48px; width: 48px">
        <!-- sidebar bottom items-->
        <div v-for="bottomItem in bottomItems" :key="bottomItem.name">
          <a
            :name="bottomItem.id"
            aria-expanded="false"
            :aria-label="bottomItem.name"
            class="list-group-item list-group-item-action d-flex align-items-center"
            :href="bottomItem.href"
            target="_blank"
          >
            <div class="d-flex justify-content-start align-items-center">
              <ips-icon v-if="bottomItem.icon" :icon="bottomItem.icon"></ips-icon>
              <span class="menu-expanded" :class="expandedClasses">{{ bottomItem.name }}</span>
            </div>
          </a>
        </div>
      </div>
      <div v-if="expandable" style="position: absolute; bottom: 0; width: 48px; left: 0">
        <a
          id="toggleSidebar"
          href="#top"
          class="list-group-item list-group-item-action d-flex align-items-center"
          aria-label="Toggle sidebar"
          @click="toggleSidebar"
        >
          <div class="d-flex justify-content-start align-items-center">
            <ips-icon id="collapseIcon" :icon="expandCollapseIcon"></ips-icon>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import IpsIcon from '@ipscape/ips-icon';

export default {
  name: 'IpsSidebar',
  components: { IpsIcon },
  props: {
    items: {
      name: 'items',
      description: 'The items of the sidebar',
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    bottomItems: {
      name: 'bottomItems',
      description: 'The bottom items of the sidebar',
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    expanded: {
      name: 'expanded',
      description: 'Expands or collapses sidebar',
      type: Boolean,
      default: false,
      required: false,
    },
    expandable: {
      name: 'expandable',
      description: 'Shows expand / collapse icons',
      type: Boolean,
      default: true,
      required: false,
    },
    homepageLink: {
      name: 'homepageLink',
      description: 'Link to open when sidebar logo is clicked',
      type: String,
      default: '',
      required: false,
    },
    selectedItem: {
      name: 'selectedItem',
      description: 'Name of selected item',
      type: String,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      selectedSidebarItem: this.selectedItem
        ? this.selectedItem
        : this.items[0]
        ? this.items[0].id
        : '',
      sidebarExpanded: this.expanded,
    };
  },
  computed: {
    sidebarContainerClasses() {
      return this.sidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed';
    },
    expandedClasses() {
      return this.sidebarExpanded ? '' : 'd-none';
    },
    expandCollapseIcon() {
      return this.sidebarExpanded ? 'ico-double-chevron-left' : 'ico-double-chevron-right';
    },
  },
  methods: {
    itemClick(itemId) {
      this.selectedSidebarItem = itemId;
      if (document.getElementById(itemId)) {
        document.getElementById(itemId).classList.toggle('collapse');
      }
      this.$emit('sidebar-item-changed', this.selectedSidebarItem);
    },
    toggleSidebar() {
      document.getElementById('sidebarContainer').classList.toggle('sidebar-expanded');
      document.getElementById('sidebarContainer').classList.toggle('sidebar-collapsed');
      if (document.getElementsByClassName('menu-expanded').classList) {
        document.getElementsByClassName('menu-expanded').classList.toggle('d-none');
      }
      if (document.getElementsByClassName('sidebar-submenu').classList) {
        document.getElementsByClassName('sidebar-submenu').classList.toggle('d-none');
      }
      // Collapse/Expand icon
      this.sidebarExpanded = !this.sidebarExpanded;
      this.$emit('toggle-sidebar', this.sidebarExpanded);
    },
    isActive(item) {
      return this.selectedSidebarItem === item;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'IpsSidebar';
</style>
